<template>
  <div>
    <v-divider class="mb-4" />

    <div class="d-flex align-center mb-3">
      <div class="text-caption text-uppercase grey--text font-weight-medium">
        <v-icon small class="mb-1">mdi-table</v-icon>
        Tableau quantitatif
      </div>
      <v-checkbox
        v-model="showDetails"
        label="Détails"
        dense
        hide-details
        class="ml-4 mt-0 pt-0 details-checkbox"
      />
    </div>

    <!-- Read-only preview while streaming -->
    <template v-if="status === 'generating'">
      <v-simple-table dense class="mb-3">
        <template #default>
          <thead>
            <tr>
              <th class="text-left pl-4" :style="{ width: codesWidth }">Codes</th>
              <th class="text-left">Désignation</th>
              <th class="text-right" style="width:90px">Qté</th>
              <th class="text-left" style="width:120px">Unité</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(m, i) in material" :key="i">
              <td class="pl-4 text-caption grey--text">
                <div v-for="c in displayCodes(m)" :key="c.value">
                  <span v-if="showDetails" class="grey--text text--lighten-1">{{ c.id_type }}</span> {{ c.value }}
                </div>
              </td>
              <td>
                <span class="d-flex align-center" style="gap:6px">
                  <v-avatar v-if="brandIcon(m)" size="20" tile>
                    <img :src="brandIcon(m)" :alt="m.manufacturer">
                  </v-avatar>
                  <v-icon v-else small color="grey lighten-1">mdi-cube-outline</v-icon>
                  {{ m.commercial_designation }}
                </span>
              </td>
              <td class="text-right">{{ m.raw_quantity }}</td>
              <td>{{ fetchRawUnit(m) }}</td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>
    </template>

    <!-- Full interface once complete -->
    <template v-else>
        <!-- Stock plan status -->
        <v-alert v-if="stockPlan === null" type="warning" dense text class="mb-3">
          Aucun plan de stock n'est configuré pour votre compte : les articles ci-dessous ne sont pas vérifiés.
        </v-alert>
        <div v-else-if="stockPlan" class="d-flex align-center flex-wrap mb-3" style="gap:8px">
          <span class="text-caption grey--text">
            <v-icon x-small class="mb-1">mdi-warehouse</v-icon>
            Plan de stock : {{ stockPlan.name }}
          </span>
          <template v-if="hasMatchInfo">
            <v-chip x-small color="success" text-color="white">{{ stockCounts.inStock }} en stock</v-chip>
            <v-chip v-if="stockCounts.toConfirm" x-small color="warning" text-color="white">{{ stockCounts.toConfirm }} à confirmer</v-chip>
            <v-chip v-if="stockCounts.outOfStock" x-small color="grey" text-color="white">{{ stockCounts.outOfStock }} hors stock</v-chip>
          </template>
          <v-spacer />
          <!-- TODO: stock re-check button hidden on purpose; remove `v-if="false"` to bring it back. -->
          <v-btn v-if="false" text x-small color="primary" :loading="rematchLoading" @click="rematch">
            <v-icon left x-small>mdi-refresh</v-icon>
            {{ hasMatchInfo ? 'Revérifier le stock' : 'Vérifier le stock' }}
          </v-btn>
        </div>

        <v-alert v-if="anyOptionApplied" type="info" dense text class="mb-3">
          Vous avez appliqué une option, pensez à supprimer les entrées qui feraient doublon.
        </v-alert>

        <div ref="tableExport">
        <v-simple-table dense class="mb-1">
          <template #default>
            <thead>
              <tr>
                <th class="text-left pl-4" :style="{ width: codesWidth }">Codes</th>
                <th class="text-left">Désignation</th>
                <th v-if="stockPlan" class="text-left" style="width:150px">Stock</th>
                <th class="text-right" style="width:190px">Qté</th>
                <th class="text-left" style="width:120px">Unité</th>
                <th style="width:44px"></th>
              </tr>
            </thead>
            <tbody>
              <template v-for="(m, i) in editingMaterial">
              <tr v-if="confirmRemoveIndex === i" :key="`confirm-${i}`" class="row-confirm-remove">
                <td :colspan="stockPlan ? 6 : 5" class="px-4 py-2">
                  <div class="d-flex align-center">
                    <span class="text-body-2 font-weight-medium">
                      Confirmer la suppression ? <span class="grey--text">{{ m.commercial_designation }}</span>
                    </span>
                    <v-spacer />
                    <v-btn small text @click="confirmRemoveIndex = null">Annuler</v-btn>
                    <v-btn small depressed color="error" class="ml-2" @click="removeRow(i)">Supprimer</v-btn>
                  </div>
                </td>
              </tr>
              <tr v-else :key="i" :class="{ 'row-out-of-stock': m.match_status === 'hors_stock' }">
                <td class="pl-4 text-caption grey--text">
                  <div v-for="c in displayCodes(m)" :key="c.value">
                    <span v-if="showDetails" class="grey--text text--lighten-1">{{ c.id_type }}</span> {{ c.value }}
                  </div>
                </td>
                <td>
                  <span class="d-flex align-center" style="gap:6px">
                    <v-avatar v-if="showImages && imageSrc(m)" size="40" tile class="material-thumb">
                      <img :src="imageSrc(m)" :alt="m.commercial_designation" loading="lazy">
                    </v-avatar>
                    <v-avatar v-else-if="brandIcon(m)" size="20" tile>
                      <img :src="brandIcon(m)" :alt="m.manufacturer">
                    </v-avatar>
                    <v-icon v-else small color="grey lighten-1">mdi-cube-outline</v-icon>
                    <span>
                      {{ m.commercial_designation }}
                      <div
                        v-if="showDetails && m.requested_designation && m.requested_designation !== m.commercial_designation"
                        class="text-caption grey--text"
                      >
                        Besoin : {{ m.requested_designation }}
                      </div>
                    </span>
                  </span>
                </td>
                <td v-if="stockPlan">
                  <div class="d-flex align-center" style="gap:4px">
                    <v-chip v-if="stockChip(m)" x-small :color="stockChip(m).color" text-color="white">
                      {{ stockChip(m).label }}
                    </v-chip>
                    <v-menu
                      v-if="m.alternatives && m.alternatives.length"
                      :value="altMenuIndex === i"
                      offset-y
                      max-width="460"
                      min-width="300"
                      :close-on-content-click="false"
                      @input="onAltMenuInput(i, $event)"
                    >
                      <template #activator="{ on, attrs }">
                        <v-btn icon x-small v-bind="attrs" v-on="on" :loading="pickingIndex === i" title="Alternatives en stock">
                          <v-icon small>mdi-swap-horizontal</v-icon>
                        </v-btn>
                      </template>
                      <v-list dense>
                        <v-subheader class="d-flex align-center" style="gap:4px">
                          <span>Alternatives en stock</span>
                          <span v-if="m.match_score != null" class="text-caption grey--text">
                            (sélection actuelle : {{ m.match_score }}%)
                          </span>
                          <v-btn icon x-small title="Rechercher dans le plan de stock" @click.stop="toggleAltSearch">
                            <v-icon small>mdi-magnify</v-icon>
                          </v-btn>
                        </v-subheader>
                        <div v-if="altSearchOpen" class="px-3 pb-2">
                          <v-text-field
                            ref="altSearchField"
                            v-model="altQuery"
                            label="Rechercher (3 caractères min.)"
                            autofocus
                            clearable
                            hide-details
                            dense
                            outlined
                            @input="onAltQueryChange"
                            @click:clear="resetAltSearch"
                          />
                        </div>
                        <template v-if="altSearchOpen">
                          <v-list-item v-for="alt in altResults" :key="alt.stock_item_id" @click="pickAltAndClose(i, alt)">
                            <v-list-item-content>
                              <v-list-item-title>{{ alt.designation }}</v-list-item-title>
                              <v-list-item-subtitle>
                                {{ alt.external_id }}<span v-if="alt.unit"> · {{ alt.unit }}</span>
                                <span v-if="!alt.is_stocked"> · Commandable (non stocké)</span>
                              </v-list-item-subtitle>
                            </v-list-item-content>
                            <v-list-item-action v-if="alt.score != null" class="ma-0">
                              <v-chip x-small outlined :color="scoreColor(alt.score)" title="Score de confiance">
                                {{ Math.round(alt.score) }}%
                              </v-chip>
                            </v-list-item-action>
                          </v-list-item>
                          <div v-if="altShowingResults && altLoading" class="text-center py-3">
                            <v-progress-circular indeterminate size="20" width="2" color="primary" />
                          </div>
                          <div v-else-if="altShowingResults && !altResults.length && altSearched" class="text-caption grey--text px-4 py-3">
                            Aucun article trouvé dans votre plan de stock.
                          </div>
                        </template>
                        <template v-else>
                          <v-list-item v-for="alt in m.alternatives" :key="alt.stock_item_id" @click="pickAltAndClose(i, alt)">
                            <v-list-item-content>
                              <v-list-item-title>{{ alt.designation }}</v-list-item-title>
                              <v-list-item-subtitle>
                                {{ alt.external_id }}<span v-if="alt.unit"> · {{ alt.unit }}</span>
                                <span v-if="!alt.is_stocked"> · Commandable (non stocké)</span>
                              </v-list-item-subtitle>
                            </v-list-item-content>
                            <v-list-item-action v-if="alt.score != null" class="ma-0">
                              <v-chip x-small outlined :color="scoreColor(alt.score)" title="Score de confiance">
                                {{ Math.round(alt.score) }}%
                              </v-chip>
                            </v-list-item-action>
                          </v-list-item>
                        </template>
                      </v-list>
                    </v-menu>
                  </div>
                </td>
                <td class="text-right py-1" style="width:190px">
                  <v-text-field
                    v-model.number="m.raw_quantity"
                    type="number"
                    class="qty-input"
                    dense
                    hide-details
                    solo
                    flat
                    style="max-width:80px;margin-left:auto"
                  />
                </td>
                <td>{{ fetchRawUnit(m) }}</td>
                <td class="px-1">
                  <v-btn icon x-small title="Supprimer la ligne" @click="confirmRemoveIndex = i">
                    <v-icon small color="error">mdi-close</v-icon>
                  </v-btn>
                </td>
              </tr>
              </template>
            </tbody>
          </template>
        </v-simple-table>
        </div>
        <div class="mb-3">
          <v-btn text small @click="openAddRowDialog">
            <v-icon left small>mdi-plus</v-icon>
            Ajouter une ligne
          </v-btn>
        </div>

        <!-- Add row dialog -->
        <v-dialog v-model="addRowDialog" max-width="480" @keydown.esc="closeAddRowDialog">
          <v-card>
            <v-card-title class="text-subtitle-1 pb-2">Ajouter une ligne</v-card-title>
            <v-card-text class="pb-0">
              <v-alert v-if="stockPlan === null" type="info" dense text class="mb-2">
                Aucun plan de stock n'est configuré pour votre compte : la recherche d'articles est indisponible.
              </v-alert>
              <v-text-field
                v-model="addRowQuery"
                label="Rechercher dans votre plan de stock"
                autofocus
                clearable
                hide-details
                dense
                outlined
                :disabled="stockPlan === null"
                @input="onAddRowQueryChange"
              />
              <v-list v-if="addRowSuggestions.length" dense class="mt-2">
                <v-list-item
                  v-for="s in addRowSuggestions"
                  :key="s.stock_item_id"
                  @click="addRowFromSuggestion(s)"
                >
                  <v-list-item-content>
                    <v-list-item-title>{{ s.designation }}</v-list-item-title>
                    <v-list-item-subtitle>
                      {{ s.external_id }}<span v-if="s.supplier_name"> · {{ s.supplier_name }}</span>
                      <span v-if="!s.is_stocked"> · Commandable (non stocké)</span>
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
              <div v-else-if="addRowLoading" class="text-center py-3">
                <v-progress-circular indeterminate size="20" width="2" color="primary" />
              </div>
              <div v-else-if="addRowQuery && addRowQuery.length >= 3 && addRowSearched" class="text-caption grey--text py-3">
                Aucun article trouvé dans votre plan de stock.
              </div>
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn text small @click="closeAddRowDialog">Annuler</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <template v-if="options.length">
          <v-divider class="mb-1" />
          <div
            class="py-2 d-flex align-center justify-space-between options-toggle"
            @click="optionsExpanded = !optionsExpanded"
          >
            <span class="text-caption text-uppercase grey--text font-weight-medium">Options disponibles</span>
            <v-icon small>{{ optionsExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
          </div>
          <template v-if="optionsExpanded">
            <div v-for="(opt, oi) in options" :key="oi" class="option-block mb-3 pa-2">
              <div class="d-flex align-center justify-space-between mb-1">
                <span class="font-weight-medium text-body-2">{{ opt.title }}</span>
                <v-chip v-if="appliedOptions[oi]" x-small color="success" text-color="white">
                  <v-icon left x-small>mdi-check</v-icon>
                  Option appliquée
                </v-chip>
                <v-btn v-else icon small @click="addOptionToMaterial(opt, oi)" color="primary">
                  <v-icon small>mdi-plus</v-icon>
                </v-btn>
              </div>
              <p v-if="opt.short_description || opt.description" class="text-body-2 grey--text mb-2">
                {{ opt.short_description || opt.description }}
              </p>
              <v-simple-table v-if="opt.material && opt.material.length" dense>
                <template #default>
                  <thead>
                    <tr>
                      <th class="text-left">Désignation</th>
                      <th class="text-right" style="width:170px">Qté</th>
                      <th class="text-left" style="width:100px">Unité</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(m, ri) in opt.material" :key="ri">
                      <td>{{ m.commercial_designation }}</td>
                      <td class="text-right"><b>{{ m.raw_quantity }}</b></td>
                      <td><b>{{ fetchRawUnit(m) }}</b></td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </div>
          </template>
        </template>

      <!-- Save / export actions -->
      <div class="d-flex align-center" style="gap:8px">
        <v-btn outlined color="primary" elevation="0" small @click="saveMaterial">
          <v-icon left small>mdi-check</v-icon>
          Enregistrer
        </v-btn>
        <v-btn outlined color="secondary" elevation="0" small @click="exportCsv">
          <v-icon left small>mdi-table-arrow-down</v-icon>
          Export CSV
        </v-btn>
        <v-btn outlined color="secondary" elevation="0" small :loading="pdfLoading" @click="exportPdf">
          <v-icon left small>mdi-file-pdf-box</v-icon>
          Export PDF
        </v-btn>
        <v-btn outlined color="secondary" elevation="0" small @click="saveMaterial">
          <v-icon left small>mdi-database-import-outline</v-icon>
          Export ERP
        </v-btn>
      </div>
    </template>
  </div>
</template>

<script>
import { resolveMediaUrl } from '@/services/mediaUrl'

const UNITS = { M2: 'm²', M3: 'm³', 'M': 'm', ML: 'm lin.', KG: 'kg', PAQ: 'paquet', BTE: 'boîte', PIE: 'pièce ' }

export default {
  name: 'MaterialTable',
  props: {
    material: { type: Array, default: () => [] },
    options: { type: Array, default: () => [] },
    status: { type: String, required: true },
    projectId: { type: [String, Number], default: null },
  },
  data() {
    return {
      materialInitialized: false,
      editingMaterial: [],
      appliedOptions: {},
      optionsExpanded: false,
      rematchLoading: false,
      pickingIndex: null,
      confirmRemoveIndex: null,
      addRowDialog: false,
      addRowQuery: '',
      addRowSuggestions: [],
      addRowLoading: false,
      addRowSearched: false,
      addRowTimer: null,
      altMenuIndex: null,
      altSearchOpen: false,
      altQuery: '',
      altResults: [],
      altLoading: false,
      altSearched: false,
      altTimer: null,
      pdfLoading: false,
      brands: [],
      // line_id -> raw_quantity at the last init/save, to log quantity edits.
      savedQuantities: {},
    }
  },
  computed: {
    altShowingResults() {
      return this.altSearchOpen && !!this.altQuery && this.altQuery.length >= 3
    },
    codesWidth() {
      return this.showDetails ? '170px' : '110px'
    },
    showDetails: {
      get() { return this.$store.getters['settings/showMaterialDetails'] },
      set(value) { this.$store.dispatch('settings/toggleMaterialDetails', value) },
    },
    showImages() {
      return this.$store.getters['settings/showProductImages']
    },
    // undefined while loading, null when the user has no stock plan.
    stockPlan() {
      return this.$store.getters['project/stockPlan']
    },
    stockMatchSeq() {
      return this.$store.state.project.stockMatchSeq
    },
    hasMatchInfo() {
      return this.editingMaterial.some(m => m.match_status)
    },
    stockCounts() {
      const counts = { inStock: 0, toConfirm: 0, outOfStock: 0 }
      this.editingMaterial.forEach(m => {
        if (m.match_status === 'matched' || m.match_status === 'manual') counts.inStock += 1
        else if (m.match_status === 'ambiguous') counts.toConfirm += 1
        else if (m.match_status === 'hors_stock') counts.outOfStock += 1
      })
      return counts
    },
    anyOptionApplied() {
      return Object.keys(this.appliedOptions).length > 0
    },
    brandIconByName() {
      const map = {}
      this.brands.forEach(b => {
        if (b.icon) map[b.name.trim().toLowerCase()] = b.icon
      })
      return map
    },
  },
  watch: {
    material: {
      immediate: true,
      handler(val) {
        if (val && val.length && !this.materialInitialized) {
          this.materialInitialized = true
          this.initMaterial()
        }
      },
    },
    status(val, old) {
      if (old === 'generating' && val !== 'generating') {
        this.materialInitialized = false
        if (this.material && this.material.length) {
          this.materialInitialized = true
          this.initMaterial()
        }
      }
    },
    // The server re-resolved materials against the stock plan (end of the
    // brief stream, or "Revérifier le stock"): rebuild the editable table.
    stockMatchSeq() {
      if (this.status !== 'generating' && this.material && this.material.length) {
        this.materialInitialized = true
        this.initMaterial()
      }
    },
  },
  created() {
    this.loadBrands()
    if (this.stockPlan === undefined) this.$store.dispatch('project/loadStockPlan')
  },
  methods: {
    async loadBrands() {
      try {
        const { fetchBrands } = await import('@/services/brandApi')
        this.brands = await fetchBrands()
      } catch (e) {
        this.brands = []
      }
    },
    brandIcon(row) {
      if (!row.manufacturer) return null
      return this.brandIconByName[row.manufacturer.trim().toLowerCase()] || null
    },
    fetchRawUnit(row) {
      return UNITS[row.raw_unit]
    },
    // Every code of a line (EAN, supplier code, article code…), deduplicated.
    imageSrc(row) {
      return resolveMediaUrl(row.image_url)
    },
    fetchCodes(row) {
      const codes = []
      const seen = new Set()
      const add = (id_type, value) => {
        const v = value == null ? '' : String(value).trim()
        if (!v || seen.has(v)) return
        seen.add(v)
        codes.push({ id_type: id_type || 'Code', value: v })
      }
      add('PDS', row.code_pds)
      add('EAN', row.barcode)
      ;(row.identifiers || []).forEach(i => add(i.id_type, i.value))
      add('Code article', row.code_article)
      add('Réf. fournisseur', row.supplier_reference)
      return codes
    },
    // Codes shown in the table: all of them in detail mode, otherwise the bare PDS code.
    displayCodes(row) {
      const codes = this.fetchCodes(row)
      return this.showDetails ? codes : codes.filter(c => c.id_type === 'PDS')
    },
    codesText(row) {
      return this.fetchCodes(row).map(c => `${c.id_type}: ${c.value}`).join('\n')
    },
    // Colour for a 0–100 match confidence score.
    scoreColor(score) {
      if (score >= 80) return 'success'
      if (score >= 60) return 'warning'
      return 'grey'
    },
    stockChip(row) {
      switch (row.match_status) {
        case 'matched':
          return { label: 'En stock', color: 'success' }
        case 'manual':
          return row.is_stocked === false
            ? { label: 'Commandable', color: 'info' }
            : { label: 'En stock', color: 'success' }
        case 'ambiguous':
          return { label: 'À confirmer', color: 'warning' }
        case 'hors_stock':
          return { label: 'Hors stock', color: 'grey' }
        default:
          return null
      }
    },
    initMaterial() {
      // _srcIndex = position in the persisted brief, used to persist a stock
      // item choice server-side. Additions and deletions are persisted too
      // (see syncMaterial); quantity edits stay local until "Enregistrer" and
      // are only logged (features/optimisation/material_events.md).
      this.editingMaterial = this.material.map((m, i) => ({
        ...m,
        sales_unit: m.sales_unit || 'PIE',
        _srcIndex: i,
      }))
      this.rememberQuantities()
      this.appliedOptions = {}
      this.optionsExpanded = false
    },
    async rematch() {
      this.rematchLoading = true
      try {
        await this.$store.dispatch('project/rematchStock')
      } finally {
        this.rematchLoading = false
      }
    },
    // Fields copied onto a row when the user explicitly chooses a stock item.
    rowFromStockItem(row, item) {
      return {
        ...row,
        requested_designation: row.requested_designation || row.commercial_designation,
        commercial_designation: item.designation,
        catalog_designation: item.designation,
        code_article: item.external_id,
        code_pds: item.external_id,
        image_url: item.image_url || null,
        supplier_name: item.supplier_name,
        supplier_reference: item.supplier_reference,
        barcode: item.barcode,
        sales_unit: item.unit || row.sales_unit,
        stock_unit: item.stock_unit,
        stock_item_id: item.stock_item_id,
        is_stocked: item.is_stocked,
        identifiers: [
          { id_type: 'EAN', value: item.barcode },
          { id_type: 'Code article', value: item.external_id },
          { id_type: 'Réf. fournisseur', value: item.supplier_reference },
        ].filter(i => i.value),
        match_status: 'manual',
        match_score: null,
        alternatives: (row.alternatives || []).filter(a => a.stock_item_id !== item.stock_item_id),
      }
    },
    onAltMenuInput(i, open) {
      this.resetAltSearch()
      this.altMenuIndex = open ? i : null
    },
    resetAltSearch() {
      clearTimeout(this.altTimer)
      this.altSearchOpen = false
      this.altQuery = ''
      this.altResults = []
      this.altLoading = false
      this.altSearched = false
    },
    toggleAltSearch() {
      // Closing the search box restores the original suggestions.
      if (this.altSearchOpen) this.resetAltSearch()
      else this.altSearchOpen = true
    },
    onAltQueryChange(val) {
      clearTimeout(this.altTimer)
      this.altResults = []
      this.altSearched = false
      if (!val || val.length < 3) { this.altLoading = false; return }
      this.altLoading = true
      this.altTimer = setTimeout(async () => {
        try {
          const { searchStock } = await import('@/services/stockApi')
          const data = await searchStock(val, this.projectId)
          if (this.altQuery === val) this.altResults = data.results || []
        } catch (e) {
          this.altResults = []
        } finally {
          if (this.altQuery === val) {
            this.altLoading = false
            this.altSearched = true
          }
        }
      }, 300)
    },
    async pickAltAndClose(i, alt) {
      this.altMenuIndex = null
      this.resetAltSearch()
      await this.pickAlternative(i, alt)
    },
    async pickAlternative(i, alt) {
      const row = this.editingMaterial[i]
      if (row._srcIndex === undefined || !this.projectId) {
        this.editingMaterial.splice(i, 1, this.rowFromStockItem(row, alt))
        return
      }
      this.pickingIndex = i
      try {
        const line = await this.$store.dispatch('project/pickStockItem', {
          projectId: this.projectId,
          index: row._srcIndex,
          stockItemId: alt.stock_item_id,
        })
        if (line) {
          // Keep the local quantity edit; take everything else from the server.
          this.editingMaterial.splice(i, 1, { ...line, raw_quantity: row.raw_quantity, _srcIndex: row._srcIndex })
        }
      } finally {
        this.pickingIndex = null
      }
    },
    rememberQuantities() {
      this.savedQuantities = Object.fromEntries(
        this.editingMaterial.filter(m => m.line_id).map(m => [m.line_id, m.raw_quantity]),
      )
    },
    // Rebuild the table from the saved one: keep the local row (and its
    // unsaved quantity) of lines we already have, take new lines from the
    // server, and refresh every row's position in the persisted brief.
    syncMaterial(serverMaterial) {
      const local = Object.fromEntries(
        this.editingMaterial.filter(m => m.line_id).map(m => [m.line_id, m]),
      )
      this.editingMaterial = serverMaterial.map((m, i) => ({
        ...(local[m.line_id] || { ...m, sales_unit: m.sales_unit || 'PIE' }),
        _srcIndex: i,
      }))
      this.savedQuantities = {
        ...Object.fromEntries(serverMaterial.map(m => [m.line_id, m.raw_quantity])),
        ...this.savedQuantities,
      }
    },
    saveMaterial() {
      this.$store.commit('project/SET_MATERIAL', this.editingMaterial)
      this.logQuantityEdits()
    },
    // Log quantity edits since the last save (audit only, fire-and-forget).
    logQuantityEdits() {
      if (!this.projectId) return
      const events = this.editingMaterial
        .filter(m => m.line_id && m.line_id in this.savedQuantities
          && Number(m.raw_quantity) !== Number(this.savedQuantities[m.line_id]))
        .map(m => ({
          action: 'edit',
          line_id: m.line_id,
          before: { line_id: m.line_id, raw_quantity: this.savedQuantities[m.line_id] },
          after: { line_id: m.line_id, raw_quantity: m.raw_quantity },
        }))
      if (!events.length) return
      this.$store.dispatch('project/logMaterialEvents', { projectId: this.projectId, events })
      this.rememberQuantities()
    },
    async removeRow(i) {
      const row = this.editingMaterial[i]
      this.confirmRemoveIndex = null
      this.editingMaterial.splice(i, 1)
      if (!this.projectId || !row.line_id) return
      const result = await this.$store.dispatch('project/removeMaterialLine', {
        projectId: this.projectId,
        lineId: row.line_id,
      })
      if (result === null) this.editingMaterial.splice(i, 0, row) // failed: put it back
      else if (result.material) this.syncMaterial(result.material)
    },
    openAddRowDialog() {
      this.addRowDialog = true
      this.addRowQuery = ''
      this.addRowSuggestions = []
      this.addRowLoading = false
      this.addRowSearched = false
    },
    closeAddRowDialog() {
      this.addRowDialog = false
      clearTimeout(this.addRowTimer)
      this.addRowSuggestions = []
      this.addRowLoading = false
    },
    onAddRowQueryChange(val) {
      clearTimeout(this.addRowTimer)
      this.addRowSuggestions = []
      this.addRowSearched = false
      if (!val || val.length < 3) { this.addRowLoading = false; return }
      this.addRowLoading = true
      this.addRowTimer = setTimeout(async () => {
        try {
          const { searchStock } = await import('@/services/stockApi')
          const data = await searchStock(val, this.projectId)
          this.addRowSuggestions = data.results || []
        } catch (e) {
          this.addRowSuggestions = []
        } finally {
          this.addRowLoading = false
          this.addRowSearched = true
        }
      }, 300)
    },
    async addRowFromSuggestion(item) {
      if (this.projectId) {
        const data = await this.$store.dispatch('project/addMaterialLines', {
          projectId: this.projectId,
          body: { lines: [{ stock_item_id: item.stock_item_id, raw_quantity: 0 }] },
        })
        if (!data) return
        this.syncMaterial(data.material)
        this.closeAddRowDialog()
        return
      }
      const unit = item.unit || 'PIE'
      this.editingMaterial.push(this.rowFromStockItem({
        commercial_designation: item.designation,
        raw_unit: unit,
        raw_quantity: 0,
        alternatives: [],
      }, item))
      this.closeAddRowDialog()
    },
    async exportPdf() {
      this.pdfLoading = true
      await this.$nextTick()
      try {
        const [{ jsPDF }, { default: autoTable }] = await Promise.all([
          import('jspdf'),
          import('jspdf-autotable'),
        ])

        const logoBase64 = await fetch(this.$theme.assets.logo)
          .then(r => r.blob())
          .then(b => new Promise(resolve => {
            const reader = new FileReader()
            reader.onload = e => resolve(e.target.result)
            reader.readAsDataURL(b)
          }))

        const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
        const pageW = doc.internal.pageSize.getWidth()
        const pageH = doc.internal.pageSize.getHeight()
        const margin = 15

        // Logo
        // Fit inside 40x15 mm box, keeping the theme logo's aspect ratio
        const { width: imgW, height: imgH } = doc.getImageProperties(logoBase64)
        const scale = Math.min(40 / imgW, 15 / imgH)
        doc.addImage(logoBase64, 'PNG', margin, margin, imgW * scale, imgH * scale)

        // DEVIS title block (right-aligned)
        const today = new Date().toLocaleDateString('fr-FR')
        const projectTitle = this.$store.state.project.currentProject?.title || ''
        doc.setFont('helvetica', 'bold')
        doc.setFontSize(22)
        doc.setTextColor(21, 101, 192)
        doc.text('DEVIS', pageW - margin, margin + 7, { align: 'right' })
        doc.setFont('helvetica', 'normal')
        doc.setFontSize(9)
        doc.setTextColor(100)
        doc.text(`N° ${this.projectId || '—'}`, pageW - margin, margin + 13, { align: 'right' })
        doc.text(`Date : ${today}`, pageW - margin, margin + 18, { align: 'right' })

        // Divider
        let y = margin + 24
        doc.setDrawColor(200)
        doc.setLineWidth(0.3)
        doc.line(margin, y, pageW - margin, y)
        y += 7

        // Project title
        if (projectTitle) {
          doc.setFont('helvetica', 'bold')
          doc.setFontSize(11)
          doc.setTextColor(30)
          doc.text(`Projet : ${projectTitle}`, margin, y)
          y += 9
        }

        // Material table
        const rows = this.editingMaterial.map((m, i) => [
          i + 1,
          this.codesText(m) || '—',
          m.commercial_designation || '',
          m.raw_quantity ?? '',
          this.fetchRawUnit(m) || '',
        ])

        autoTable(doc, {
          startY: y,
          margin: { left: margin, right: margin },
          head: [['#', 'Codes', 'Désignation', 'Qté', 'Unité']],
          body: rows,
          styles: { fontSize: 9, cellPadding: 3 },
          headStyles: { fillColor: [21, 101, 192], textColor: 255, fontStyle: 'bold' },
          columnStyles: {
            0: { cellWidth: 8, halign: 'center' },
            1: { cellWidth: 48 },
            2: { cellWidth: 'auto' },
            3: { cellWidth: 18, halign: 'right' },
            4: { cellWidth: 22 },
          },
          alternateRowStyles: { fillColor: [245, 247, 250] },
        })

        // Page footers
        const pageCount = doc.internal.getNumberOfPages()
        for (let i = 1; i <= pageCount; i++) {
          doc.setPage(i)
          doc.setFont('helvetica', 'normal')
          doc.setFontSize(8)
          doc.setTextColor(150)
          doc.text(`Page ${i} / ${pageCount}`, pageW - margin, pageH - 8, { align: 'right' })
          doc.text(this.$theme.brand.pdfFooter, margin, pageH - 8)
        }

        doc.save(`devis_${this.projectId || 'export'}.pdf`)
      } finally {
        this.pdfLoading = false
      }
    },
    exportCsv() {
      const rows = [
        ['Codes', 'Désignation', 'Quantité', 'Unité'],
        ...this.editingMaterial.map(m => [
          this.codesText(m),
          m.commercial_designation || '',
          m.raw_quantity ?? '',
          this.fetchRawUnit(m) || '',
        ]),
      ]
      const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n')
      const blob = new Blob([csv], { type: 'text/csv' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'materiaux.csv'
      a.click()
      URL.revokeObjectURL(url)
    },
    async addOptionToMaterial(opt, oi) {
      if (this.projectId) {
        const data = await this.$store.dispatch('project/addMaterialLines', {
          projectId: this.projectId,
          body: { option_index: oi },
        })
        if (!data) return
        this.syncMaterial(data.material)
        this.$set(this.appliedOptions, oi, true)
        return
      }
      const rows = (opt.material || []).map(m => ({
        ...m,
        sales_unit: m.sales_unit || 'PIE',
      }))
      this.editingMaterial.push(...rows)
      this.$set(this.appliedOptions, oi, true)
    },
  },
}
</script>

<style lang="scss" scoped>
.material-thumb {
  border: 1px solid rgba(128, 128, 128, 0.25);
  border-radius: 4px;
  background: #fff;
  img {
    object-fit: contain;
  }
}
.row-out-of-stock td {
  opacity: 0.6;
}
.qty-input ::v-deep input {
  text-align: right;
  -moz-appearance: textfield;
}
.qty-input ::v-deep input::-webkit-outer-spin-button,
.qty-input ::v-deep input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.details-checkbox ::v-deep .v-label {
  font-size: 0.75rem;
}
.row-confirm-remove td {
  background: rgba(244, 67, 54, 0.08);
}
.option-block {
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 6px;
}
.options-toggle {
  cursor: pointer;
  user-select: none;
  &:hover {
    background: rgba(0, 0, 0, 0.03);
  }
}
</style>
